<template>
  <div v-if="auth.can('HOME_PAGE')" class="dash">
    <!-- ── Page header + period filter ─────────────────────────────── -->
    <header class="dash-hero">
      <div class="dash-hero-text">
        <span class="dash-eyebrow">Statistiques générales</span>
        <h1 class="dash-title">Bonjour{{ firstName ? `, ${firstName}` : "" }}</h1>
        <p class="dash-sub">
          {{ todayLabel }}
          <template v-if="lastUpdated"> · Mis à jour à {{ lastUpdated }}</template>
        </p>
      </div>

      <div class="dash-period">
        <div class="dash-segment" role="group" aria-label="Période">
          <button
            v-for="option in periodOptions"
            :key="option.key"
            type="button"
            class="dash-segment-btn"
            :class="{ active: period === option.key }"
            :aria-pressed="period === option.key"
            @click="setPeriod(option.key)"
          >
            {{ option.label }}
          </button>
        </div>
        <button
          type="button"
          class="ui-icon-btn dash-refresh"
          :class="{ spinning: isLoader }"
          aria-label="Actualiser les statistiques"
          title="Actualiser"
          :disabled="isLoader"
          @click="refreshAll"
        >
          <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <!-- Custom range -->
    <transition name="dash-slide">
      <form v-if="period === 'custom'" class="dash-range" @submit.prevent="FilterData">
        <label class="dash-range-field">
          <span>Date début</span>
          <input v-model="searchSDate" type="date" class="form-control" :max="searchEDate || undefined" />
        </label>
        <label class="dash-range-field">
          <span>Date fin</span>
          <input v-model="searchEDate" type="date" class="form-control" :min="searchSDate || undefined" />
        </label>
        <button type="submit" class="btn btn-primary" :disabled="isLoader">Chercher</button>
        <button type="button" class="btn btn-outline-secondary" :disabled="isLoader" @click="resetPeriod">Annuler</button>
      </form>
    </transition>

    <!-- ── Headline KPIs ─────────────────────────────────────────── -->
    <section class="kpi-grid kpi-grid--5" aria-label="Indicateurs clés">
      <article class="kpi kpi-featured" :class="{ 'is-loading': isLoader }">
        <div class="kpi-top">
          <span class="kpi-label">Total colis</span>
          <span class="kpi-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
        </div>
        <div class="kpi-value"><AnimatedNumber :value="num(dataDash.Totalcolis)" /></div>
        <p class="kpi-foot">{{ periodLabel }}</p>
      </article>

      <article class="kpi kpi-success" :class="{ 'is-loading': isLoader }">
        <div class="kpi-top">
          <span class="kpi-label">Colis ramassés</span>
          <span class="kpi-icon"><i class="ph ph-truck" aria-hidden="true"></i></span>
        </div>
        <div class="kpi-value"><AnimatedNumber :value="num(dataDash.TotalRamasse)" /></div>
        <div class="kpi-progress" :title="`${pickupRate}% des colis ramassés`">
          <span :style="{ width: pickupRate + '%' }"></span>
        </div>
        <p class="kpi-foot"><b>{{ pickupRate }}%</b> des colis ramassés</p>
      </article>

      <article class="kpi kpi-success" :class="{ 'is-loading': isLoader }">
        <div class="kpi-top">
          <span class="kpi-label">Colis livrés</span>
          <span class="kpi-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
        </div>
        <div class="kpi-value"><AnimatedNumber :value="num(dataDash.TotalLivres)" /></div>
        <p class="kpi-foot">{{ periodLabel }}</p>
      </article>

      <component
        :is="canAccessPath('/customers-list') ? 'router-link' : 'article'"
        :to="canAccessPath('/customers-list') ? '/customers-list' : undefined"
        class="kpi kpi-info"
        :class="{ 'is-loading': isLoader }"
      >
        <div class="kpi-top">
          <span class="kpi-label">Clients</span>
          <span class="kpi-icon"><i class="ph ph-users-three" aria-hidden="true"></i></span>
        </div>
        <div class="kpi-value"><AnimatedNumber :value="num(dataDash.Totalclients)" /></div>
        <p class="kpi-foot">Comptes clients</p>
      </component>

      <component
        :is="canAccessPath('/users-list') ? 'router-link' : 'article'"
        :to="canAccessPath('/users-list') ? '/users-list' : undefined"
        class="kpi"
        :class="{ 'is-loading': isLoader }"
      >
        <div class="kpi-top">
          <span class="kpi-label">Utilisateurs</span>
          <span class="kpi-icon"><i class="ph ph-user-gear" aria-hidden="true"></i></span>
        </div>
        <div class="kpi-value"><AnimatedNumber :value="num(dataDash.Totalusers)" /></div>
        <p class="kpi-foot">Équipe, livreurs et responsables</p>
      </component>
    </section>

    <!-- ── Activity groups ───────────────────────────────────────── -->
    <section v-for="group in tileGroups" :key="group.title" class="dash-section">
      <div class="dash-section-head">
        <div>
          <span class="dash-eyebrow">{{ group.eyebrow }}</span>
          <h2 class="dash-section-title">{{ group.title }}</h2>
        </div>
      </div>
      <div class="tile-grid">
        <component
          :is="tile.to && canAccessPath(tile.to) ? 'router-link' : 'div'"
          v-for="tile in group.tiles"
          :key="tile.label"
          :to="tile.to && canAccessPath(tile.to) ? tile.to : undefined"
          class="tile"
          :class="{ clickable: tile.to && canAccessPath(tile.to), 'is-loading': isLoader }"
        >
          <span class="tile-icon" :class="`tone-${tile.tone}`"><i :class="tile.icon" aria-hidden="true"></i></span>
          <span class="tile-text">
            <span class="tile-value"><AnimatedNumber :value="num(dataDash[tile.key])" /></span>
            <span class="tile-label">{{ tile.label }}</span>
          </span>
          <i v-if="tile.to && canAccessPath(tile.to)" class="ph ph-arrow-up-right tile-go" aria-hidden="true"></i>
        </component>
      </div>
    </section>

    <!-- ── Transport fees (bons d'envoi expédiés) ──────────────────── -->
    <section class="dash-section" aria-label="Frais de transport">
      <div class="dash-section-head">
        <div>
          <span class="dash-eyebrow">Expéditions</span>
          <h2 class="dash-section-title">Frais de transport</h2>
        </div>
        <span class="dash-section-hint">Bons d'envoi expédiés · {{ periodLabel }}</span>
      </div>

      <div class="kpi-grid">
        <article class="kpi kpi-featured" :class="{ 'is-loading': transportLoading }">
          <div class="kpi-top">
            <span class="kpi-label">Total frais de transport</span>
            <span class="kpi-icon"><i class="ph ph-truck-trailer" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value">
            <AnimatedNumber :value="transportFees.total.fees" :decimals="2" /><small>{{ $devis }}</small>
          </div>
          <p class="kpi-foot">{{ periodLabel }}</p>
        </article>

        <component
          :is="canAccessPath('/sent-notes') ? 'router-link' : 'article'"
          :to="canAccessPath('/sent-notes') ? '/sent-notes' : undefined"
          class="kpi kpi-info"
          :class="{ 'is-loading': transportLoading }"
        >
          <div class="kpi-top">
            <span class="kpi-label">Bons expédiés</span>
            <span class="kpi-icon"><i class="ph ph-paper-plane-tilt" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value"><AnimatedNumber :value="transportFees.total.notes" /></div>
          <p class="kpi-foot">
            <b>{{ transportFees.total.parcels }}</b> colis ·
            <b>{{ transportFees.total.sacs }}</b> sacs
          </p>
        </component>

        <article class="kpi kpi-success" :class="{ 'is-loading': transportLoading }">
          <div class="kpi-top">
            <span class="kpi-label">Coût moyen par bon</span>
            <span class="kpi-icon"><i class="ph ph-receipt" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value">
            <AnimatedNumber :value="transportFees.total.avg_per_note" :decimals="2" /><small>{{ $devis }}</small>
          </div>
          <p class="kpi-foot">
            Sur {{ transportFees.total.notes_with_fees }} bon(s) avec frais saisis
          </p>
        </article>

        <article class="kpi" :class="{ 'is-loading': transportLoading }">
          <div class="kpi-top">
            <span class="kpi-label">Coût moyen par colis</span>
            <span class="kpi-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value">
            <AnimatedNumber :value="transportFees.total.avg_per_parcel" :decimals="2" /><small>{{ $devis }}</small>
          </div>
          <p class="kpi-foot">Frais ÷ colis des bons avec frais</p>
        </article>
      </div>

      <div v-if="transportFees.by_transport.length" class="transport-breakdown">
        <div class="transport-row transport-row-head">
          <span>Transport</span>
          <span>Bons</span>
          <span>Colis</span>
          <span>Moy. / bon</span>
          <span>Total</span>
        </div>
        <div v-for="row in transportFees.by_transport" :key="row.transport_id ?? 'none'" class="transport-row">
          <span class="transport-name">
            {{ row.transport_name }}
            <span class="transport-bar" :title="`${transportShare(row.fees)}% du total`">
              <span :style="{ width: transportShare(row.fees) + '%' }"></span>
            </span>
          </span>
          <span>{{ row.notes }}</span>
          <span>{{ row.parcels }}</span>
          <span>{{ money(row.avg_per_note) }}</span>
          <span class="transport-total">{{ money(row.fees) }}</span>
        </div>
      </div>
      <p v-else-if="!transportLoading" class="dash-section-hint mt-3">
        Aucun bon d'envoi expédié sur cette période.
      </p>
    </section>

    <!-- ── Charts ─────────────────────────────────────────────────── -->
    <!-- Charts load when scrolled into view, not with the page -->
    <section class="chart-grid" :key="`charts-${refreshKey}`">
      <LazyMount>
        <ParcelCharts :period-start="searchSDate" :period-end="searchEDate" :period-version="periodVersion" />
      </LazyMount>
      <LazyMount>
        <UsersDataChart />
      </LazyMount>
    </section>
    <LazyMount :key="`months-${refreshKey}`">
      <LastMonthsChart />
    </LazyMount>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from "vue";

import ParcelCharts from "./ParcelCharts.vue";
import LastMonthsChart from "./LastMonthsChart.vue";
import UsersDataChart from "./UsersDataChart.vue";
import AnimatedNumber from "../../components/Dashboard/AnimatedNumber.vue";
import LazyMount from "../../components/Dashboard/LazyMount.vue";

import { dashboardGet, clearDashboardCache } from "@/utils/dashboardApi";
import { useAuthStore } from "@/stores/auth";
import { currency } from "@/utils/currency";
import { canAccessPath } from "@/router";

export default defineComponent({
  name: "EcommercePage",
  components: {
    ParcelCharts,
    LastMonthsChart,
    UsersDataChart,
    AnimatedNumber,
    LazyMount,
  },

  setup() {
    const auth = useAuthStore();

    const dataDash = reactive<Record<string, number>>({
      Totalcolis: 0,
      TotalRamasse: 0,
      TotalLivres: 0,
      Totalusers: 0,
      Totalclients: 0,
      TotalFournisseurs: 0,
      TotalProduits: 0,
      TotalSelfProduits: 0,
      TotalBonRecetion: 0,
      TotalBL: 0,
      TotalBLSupp: 0,
      TotalBLStock: 0,
      TotalBENV: 0,
      TotalBDisctr: 0,
      TotalBonPaie: 0,
      TotalBonZPaie: 0,
      TotalBonRetourAgence: 0,
      TotalBonRetourCust: 0,
      TotalInvoices: 0,
      TotalTransfer: 0,
      TotalClaim: 0,
      TotalRtnRequest: 0,
      TotalPickRequest: 0,
    });

    const isLoader = ref(false);
    const searchSDate = ref("");
    const searchEDate = ref("");
    const lastUpdated = ref("");
    const refreshKey = ref(0);
    // Bumped when the header period changes, so ParcelCharts refetches
    const periodVersion = ref(0);

    function FilterData() {
      periodVersion.value += 1;
      getData();
    }
    function ClearFilterData() {
      searchSDate.value = "";
      searchEDate.value = "";
      periodVersion.value += 1;
      getData();
    }
    // ── Transport fees (sent_notes.transport_fees, by shipping date) ──
    type TransportKpis = {
      fees: number;
      notes: number;
      notes_with_fees: number;
      parcels: number;
      sacs: number;
      avg_per_note: number;
      avg_per_parcel: number;
    };
    type TransportRow = TransportKpis & { transport_id: number | null; transport_name: string };
    const emptyTransportKpis = (): TransportKpis => ({
      fees: 0, notes: 0, notes_with_fees: 0, parcels: 0, sacs: 0, avg_per_note: 0, avg_per_parcel: 0,
    });
    const transportFees = reactive<{ total: TransportKpis; by_transport: TransportRow[] }>({
      total: emptyTransportKpis(),
      by_transport: [],
    });
    const transportLoading = ref(false);

    async function getTransportFees(request: number) {
      transportLoading.value = true;
      try {
        const data = await dashboardGet(
          `/api/auth/TransportFeesData?searchSDate=${searchSDate.value}&searchEDate=${searchEDate.value}`
        );
        if (request !== lastRequest) return; // a newer period was picked meanwhile
        transportFees.total = data.total ?? emptyTransportKpis();
        transportFees.by_transport = data.by_transport ?? [];
      } catch (error) {
        console.log(error);
      } finally {
        if (request === lastRequest) transportLoading.value = false;
      }
    }

    const moneyFormat = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const money = (v: number) => `${moneyFormat.format(Number(v) || 0)} ${currency()}`;
    const transportShare = (fees: number) => {
      const total = Number(transportFees.total.fees) || 0;
      return total ? Math.round((Number(fees) / total) * 100) : 0;
    };

    // Only the answers of the latest period are shown (clicking several
    // periods quickly must not end with an older answer on screen).
    let lastRequest = 0;

    async function getData() {
      const request = ++lastRequest;
      isLoader.value = true;
      // KPIs first; the transport fees are queued right behind them
      const counters = dashboardGet(`/api/auth/DashData?searchSDate=${searchSDate.value}&searchEDate=${searchEDate.value}`);
      getTransportFees(request);
      try {
        const data = await counters;
        if (request !== lastRequest) return;
        Object.assign(dataDash, data);
        lastUpdated.value = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
      } catch (error) {
        console.log(error);
      } finally {
        if (request === lastRequest) isLoader.value = false;
      }
    }

    // ── Period filter → the existing searchSDate / searchEDate parameters ──
    const periodOptions = [
      { key: "all", label: "Tout" },
      { key: "today", label: "Aujourd'hui" },
      { key: "yesterday", label: "Hier" },
      { key: "7d", label: "7 jours" },
      { key: "30d", label: "30 jours" },
      { key: "month", label: "Ce mois" },
      { key: "year", label: "Cette année" },
      { key: "custom", label: "Personnalisé" },
    ];
    const period = ref("today");

    // Local date (toISOString would shift the day in UTC+1)
    const ymd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    function rangeFor(key: string): [string, string] {
      const now = new Date();
      const today = ymd(now);
      const daysAgo = (n: number) => {
        const d = new Date(now);
        d.setDate(d.getDate() - n);
        return ymd(d);
      };
      switch (key) {
        case "today":
          return [today, today];
        case "yesterday":
          return [daysAgo(1), daysAgo(1)];
        case "7d":
          return [daysAgo(6), today];
        case "30d":
          return [daysAgo(29), today];
        case "month":
          return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), today];
        case "year":
          return [`${now.getFullYear()}-01-01`, today];
        default:
          return ["", ""];
      }
    }

    // Default period is "today": apply its dates before the first load
    [searchSDate.value, searchEDate.value] = rangeFor(period.value);

    function setPeriod(key: string) {
      period.value = key;
      if (key === "custom") return; // applied with the "Chercher" button
      [searchSDate.value, searchEDate.value] = rangeFor(key);
      FilterData();
    }
    function resetPeriod() {
      period.value = "all";
      ClearFilterData();
    }
    function refreshAll() {
      clearDashboardCache();
      getData();
      refreshKey.value += 1; // remounts the chart widgets so they refetch
    }

    const periodLabel = computed(() => {
      if (period.value === "custom" && searchSDate.value) {
        const f = (s: string) => new Date(s + "T00:00:00").toLocaleDateString("fr-FR");
        return `Du ${f(searchSDate.value)}${searchEDate.value ? ` au ${f(searchEDate.value)}` : ""}`;
      }
      const option = periodOptions.find((o) => o.key === period.value);
      return option && option.key !== "all" ? option.label : "Depuis le début";
    });

    // ── Formatting & derived figures ──
    const num = (v: any) => {
      const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    };
    const pickupRate = computed(() => {
      const total = num(dataDash.Totalcolis);
      return total ? Math.min(100, Math.round((num(dataDash.TotalRamasse) / total) * 100)) : 0;
    });

    const userName = computed(() => (auth.user as any)?.name || "");
    const firstName = computed(() => userName.value.trim().split(/\s+/)[0] || "");
    const todayLabel = (() => {
      const s = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      return s.charAt(0).toUpperCase() + s.slice(1);
    })();

    // Same 19 counters as the former StatsBoxes, grouped by domain
    const tileGroups = [
      {
        eyebrow: "Logistique",
        title: "Opérations",
        tiles: [
          { key: "TotalBonRecetion", label: "Bons de réception", icon: "ph ph-clipboard-text", tone: "accent", to: "/reception-notes" },
          { key: "TotalBL", label: "Bons de livraison", icon: "ph ph-file-text", tone: "accent", to: "/delivery-notes" },
          { key: "TotalBLStock", label: "Bons de livraison stock", icon: "ph ph-stack", tone: "accent", to: "/delivery-stock-notes" },
          { key: "TotalBENV", label: "Bons d'envoi", icon: "ph ph-paper-plane-tilt", tone: "info", to: "/sent-notes" },
          { key: "TotalBDisctr", label: "Bons de distribution", icon: "ph ph-cube", tone: "info", to: "/distribution-notes" },
          { key: "TotalProduits", label: "Produits en stock", icon: "ph ph-tag", tone: "success", to: "/products-list" },
        ],
      },
      {
        eyebrow: "Argent",
        title: "Finances",
        tiles: [
          { key: "TotalBonPaie", label: "Bons de paiement (livreur)", icon: "ph ph-moped", tone: "success", to: "/payment-note-liste" },
          { key: "TotalBonZPaie", label: "Bons de paiement (zone)", icon: "ph ph-map-pin", tone: "success", to: "/payment-zone-liste" },
          { key: "TotalInvoices", label: "Factures", icon: "ph ph-receipt", tone: "info", to: "/invoices-liste" },
          { key: "TotalTransfer", label: "Virements", icon: "ph ph-bank", tone: "info", to: "/virements-liste" },
        ],
      },
      {
        eyebrow: "Service",
        title: "Retours et demandes",
        tiles: [
          { key: "TotalBonRetourCust", label: "Bons de retour (client)", icon: "ph ph-arrow-counter-clockwise", tone: "danger", to: "/return-client-liste" },
          { key: "TotalBonRetourAgence", label: "Bons de retour (agence)", icon: "ph ph-arrow-u-up-left", tone: "danger", to: "/return-zone-liste" },
          { key: "TotalClaim", label: "Réclamations", icon: "ph ph-warning-circle", tone: "warning", to: "/claims-list" },
          { key: "TotalRtnRequest", label: "Demandes de retour", icon: "ph ph-arrow-bend-up-left", tone: "warning" },
          { key: "TotalPickRequest", label: "Demandes de ramassage", icon: "ph ph-hand-coins", tone: "warning", to: "/requests-pickup-list" },
        ],
      },
    ];

    onMounted(() => {
      if (auth.can("HOME_PAGE")) getData();
    });

    return {
      FilterData,
      ClearFilterData,
      getData,
      isLoader,
      searchSDate,
      searchEDate,
      dataDash,
      auth,
      canAccessPath,
      lastUpdated,
      refreshKey,
      periodVersion,
      periodOptions,
      period,
      setPeriod,
      resetPeriod,
      refreshAll,
      periodLabel,
      num,
      pickupRate,
      firstName,
      todayLabel,
      tileGroups,
      transportFees,
      transportLoading,
      money,
      transportShare,
    };
  },
});
</script>

<style scoped>
/* All values come from the design tokens (assets/css/theme/_tokens.scss).
   Shared panel primitives live in assets/css/theme/_dashboard.scss. */
.dash {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-6);
  padding-bottom: var(--ui-space-6);
}

/* ── Header ─────────────────────────────────────────────────────── */
.dash-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--ui-space-4);
  flex-wrap: wrap;
  padding-bottom: var(--ui-space-5);
  border-bottom: 1px solid var(--ui-border);
}
.dash-title {
  margin: 0;
  color: var(--ui-text);
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.dash-sub {
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.dash-period {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
}
.dash-segment {
  display: flex;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow-x: auto;
  scrollbar-width: none;
}
.dash-segment::-webkit-scrollbar {
  display: none;
}
.dash-segment-btn {
  flex: 0 0 auto;
  padding: 8px 14px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease),
    box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.dash-segment-btn:hover:not(.active) {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.dash-segment-btn.active {
  background: var(--ui-accent);
  color: #FFFFFF;
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.28);
}
.dash-refresh.spinning i {
  animation: dash-spin 0.9s linear infinite;
}
@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

.dash-range {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: calc(var(--ui-space-2) * -1);
}
.dash-range-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.dash-range-field .form-control {
  min-width: 170px;
}
.dash-slide-enter-active,
.dash-slide-leave-active {
  transition: opacity var(--ui-dur) var(--ui-ease), transform var(--ui-dur) var(--ui-ease);
}
.dash-slide-enter-from,
.dash-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── KPIs ───────────────────────────────────────────────────────── */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.kpi-grid--5 { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.kpi {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--ui-space-5) var(--ui-space-6);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-xl);
  background:
    radial-gradient(circle at 100% 0%, var(--tone-soft), transparent 55%),
    var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
  transition: transform var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease),
    border-color var(--ui-dur) var(--ui-ease);
}
.kpi:hover {
  transform: translateY(-3px);
  border-color: var(--tone);
  box-shadow: var(--ui-shadow-card-hover);
}
.kpi-success { --tone: var(--ui-success); --tone-soft: var(--ui-success-soft); }
.kpi-info { --tone: var(--ui-info); --tone-soft: var(--ui-info-soft); }
.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.kpi-label {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.kpi-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--ui-radius-sm);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 19px;
}
.kpi-value {
  margin-top: var(--ui-space-3);
  color: var(--ui-text);
  font-size: clamp(1.7rem, 2.4vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.kpi-value small {
  margin-left: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  letter-spacing: 0;
}
.kpi-foot {
  margin: auto 0 0;
  padding-top: var(--ui-space-2);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.kpi-foot b {
  color: var(--tone);
  font-weight: var(--ui-fw-black);
}
.kpi-progress {
  height: 6px;
  margin-top: var(--ui-space-3);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.kpi-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tone);
  transition: width 800ms var(--ui-ease-spring);
}
.kpi-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin: 0 5px 1px 0;
  border-radius: 50%;
}
.kpi-foot .kpi-dot:not(:first-child) {
  margin-left: 10px;
}
.kpi-dot.danger { background: var(--ui-danger); }
.kpi-dot.warning { background: var(--ui-warning); }

/* Featured KPI: solid accent card */
.kpi-featured {
  border-color: var(--ui-accent-strong);
  background:
    radial-gradient(circle at 110% -10%, rgba(255, 255, 255, 0.22) 0 35%, transparent 36%),
    radial-gradient(circle at -10% 120%, rgba(0, 0, 0, 0.1) 0 40%, transparent 41%),
    linear-gradient(145deg, var(--ui-accent-light) 0%, var(--ui-accent) 50%, var(--ui-accent-strong) 100%);
  box-shadow: var(--ui-shadow-accent);
}
.kpi-featured:hover {
  border-color: var(--ui-accent-strong);
  box-shadow: 0 16px 36px rgba(var(--ui-accent-rgb), 0.4);
}
.kpi-featured .kpi-label,
.kpi-featured .kpi-foot,
.kpi-featured .kpi-value small {
  color: rgba(255, 255, 255, 0.85);
}
.kpi-featured .kpi-value {
  color: #FFFFFF;
}
.kpi-featured .kpi-icon {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

/* Loading shimmer over the values */
.kpi.is-loading .kpi-value,
.tile-grid .tile-value {
  position: relative;
}
.kpi.is-loading .kpi-value::after {
  content: "";
  position: absolute;
  inset: 4px 30% 4px 0;
  border-radius: var(--ui-radius-xs);
  background: linear-gradient(90deg, var(--ui-surface-sunken), var(--ui-surface-hover), var(--ui-surface-sunken));
  background-size: 300% 100%;
  animation: dash-shimmer 1.2s ease-in-out infinite;
}
.kpi-featured.is-loading .kpi-value::after {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.15));
  background-size: 300% 100%;
}
.kpi.is-loading .kpi-value > * {
  opacity: 0;
}
@keyframes dash-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* ── Activity tiles ─────────────────────────────────────────────── */
.tile-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--ui-space-4);
}
.tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  color: inherit;
  text-decoration: none;
  transition: transform var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease),
    box-shadow var(--ui-dur) var(--ui-ease);
}
.tile.clickable:hover {
  transform: translateY(-2px);
  border-color: var(--ui-accent);
  box-shadow: var(--ui-shadow-card-hover);
}
.tile-icon {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 19px;
}
.tone-success { --tone: var(--ui-success); --tone-soft: var(--ui-success-soft); }
.tone-warning { --tone: var(--ui-warning-strong); --tone-soft: var(--ui-warning-soft); }
.tone-danger { --tone: var(--ui-danger); --tone-soft: var(--ui-danger-soft); }
.tone-info { --tone: var(--ui-info); --tone-soft: var(--ui-info-soft); }
.tile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tile-value {
  color: var(--ui-text);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
}
.tile-label {
  overflow: hidden;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tile-go {
  position: absolute;
  top: 10px;
  right: 10px;
  color: var(--ui-text-faint);
  font-size: 13px;
  transition: color var(--ui-dur-fast) var(--ui-ease), transform var(--ui-dur-fast) var(--ui-ease);
}
.tile.clickable:hover .tile-go {
  color: var(--ui-accent-text);
  transform: translate(2px, -2px);
}

/* ── Sections ───────────────────────────────────────────────────── */
.dash-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--ui-space-3);
  flex-wrap: wrap;
  margin-bottom: var(--ui-space-4);
}
.dash-section-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.dash-section-hint {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}

/* Outcome cards */
.outcome-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.outcome {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  display: flex;
  flex-direction: column;
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-top: 4px solid var(--tone);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.outcome-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--ui-space-4);
}
.outcome-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: var(--ui-radius-md);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 21px;
}
.outcome-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.outcome-count {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.outcome-split {
  margin-bottom: var(--ui-space-4);
}
.outcome-split-bar {
  height: 8px;
  border-radius: var(--ui-radius-pill);
  background: var(--tone-soft);
  overflow: hidden;
}
.outcome-split-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tone);
  transition: width 800ms var(--ui-ease-spring);
}
.outcome-split-legend {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.outcome-split-legend .dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 5px;
  border-radius: 50%;
}
.outcome-split-legend .dot.solid { background: var(--tone); }
.outcome-split-legend .dot.soft { background: var(--tone-soft); outline: 1px solid var(--tone); }
.outcome-rows {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px 12px;
  margin: 0;
}
.outcome-rows dt {
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-medium);
}
.outcome-rows dd {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.outcome-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: var(--ui-space-4);
  border-top: 1px dashed var(--ui-border-strong);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.outcome-rows + .outcome-total {
  margin-top: var(--ui-space-4);
}
.outcome-total b {
  color: var(--tone);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.outcome-total small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}

/* Transport fees breakdown */
.transport-breakdown {
  margin-top: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
}
.transport-row {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) repeat(3, minmax(0, 0.8fr)) minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid var(--ui-border);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  font-variant-numeric: tabular-nums;
}
.transport-row > span:not(:first-child) {
  text-align: right;
}
.transport-row-head {
  border-top: 0;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.transport-name {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  color: var(--ui-text);
  font-weight: var(--ui-fw-heavy);
}
.transport-bar {
  height: 5px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.transport-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--ui-accent);
  transition: width 800ms var(--ui-ease-spring);
}
.transport-total {
  color: var(--ui-text);
  font-weight: var(--ui-fw-black);
}
@media (max-width: 767.98px) {
  .transport-row {
    grid-template-columns: minmax(0, 1.6fr) repeat(2, minmax(0, 0.7fr)) minmax(0, 1fr);
  }
  .transport-row > span:nth-child(4) {
    display: none;
  }
}

/* Charts */
.chart-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: var(--ui-space-5);
}

/* Welcome (no stats permission) */
.dash-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  text-align: center;
}
.dash-welcome-icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: 30px;
}
.dash-welcome h2 {
  margin: 0 0 6px;
  font-size: var(--ui-fs-xl);
  font-weight: var(--ui-fw-black);
}
.dash-welcome p {
  margin: 0;
  color: var(--ui-text-muted);
}

/* ── Announcements ──────────────────────────────────────────────── */
.notice-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.notice-card {
  --notice-accent: var(--ui-info);
  --notice-soft: var(--ui-info-soft);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background:
    linear-gradient(90deg, var(--notice-soft), transparent 40%),
    var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.notice-card-success { --notice-accent: var(--ui-success); --notice-soft: var(--ui-success-soft); }
.notice-card-danger { --notice-accent: var(--ui-danger); --notice-soft: var(--ui-danger-soft); }
.notice-card-warning { --notice-accent: var(--ui-warning-strong); --notice-soft: var(--ui-warning-soft); }
.notice-card-info,
.notice-card-primary { --notice-accent: var(--ui-accent-text); --notice-soft: var(--ui-accent-soft); }
.notice-card-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--notice-soft);
  color: var(--notice-accent);
  font-size: 16px;
}
.notice-card-body {
  flex: 1 1 auto;
  color: var(--ui-text);
  font-size: var(--ui-fs-base);
}
.notice-card-body :deep(p) {
  margin: 0;
}
.notice-card-close {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ui-text-muted);
  font-size: 15px;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.notice-card-close:hover {
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}
.notice-fade-enter-active,
.notice-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.notice-fade-enter-from,
.notice-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Entrance animation (staggered) ─────────────────────────────── */
.kpi,
.tile,
.outcome {
  animation: dash-rise 520ms var(--ui-ease-spring) both;
}
.kpi:nth-child(2), .outcome:nth-child(2), .tile:nth-child(2) { animation-delay: 60ms; }
.kpi:nth-child(3), .outcome:nth-child(3), .tile:nth-child(3) { animation-delay: 120ms; }
.kpi:nth-child(4), .outcome:nth-child(4), .tile:nth-child(4) { animation-delay: 180ms; }
.tile:nth-child(5) { animation-delay: 240ms; }
.tile:nth-child(6) { animation-delay: 300ms; }
@keyframes dash-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 1399.98px) {
  .tile-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .outcome-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 1199.98px) {
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .chart-grid { grid-template-columns: minmax(0, 1fr); }
  .dash-period { width: 100%; }
  .dash-segment { flex: 1 1 auto; }
}
@media (max-width: 767.98px) {
  .tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .outcome-grid { grid-template-columns: minmax(0, 1fr); }
  .dash-range { justify-content: stretch; }
  .dash-range-field { flex: 1 1 140px; }
  .dash-range-field .form-control { min-width: 0; }
  .dash-range .btn { flex: 1 1 100%; }
}
@media (max-width: 479.98px) {
  .kpi-grid { grid-template-columns: minmax(0, 1fr); }
  .kpi { padding: var(--ui-space-4) var(--ui-space-5); }
}
@media (prefers-reduced-motion: reduce) {
  .kpi, .tile, .outcome { animation: none; }
  .dash-refresh.spinning i { animation: none; }
}

/* Admin: KPI cards can be links; tile groups have 4-6 tiles */
.kpi {
  color: inherit;
  text-decoration: none;
}
.dash-section .tile-grid {
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
}
.tile.is-loading .tile-value {
  opacity: 0.35;
}
.dash-refresh:disabled {
  cursor: progress;
}
</style>
